// ============================================
// components/ErrorPage.tsx
//
// 居中卡片式错误页. 给 FromApp 错误状态、404、详情找不到等场景复用.
// ============================================

import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

interface Props {
  /** 大图标,可传 lucide 图标组件如 <Search />,默认警告图标 */
  icon?: React.ReactNode;
  /** 标题文字 */
  title: string;
  /** 描述,可换行 */
  message?: string;
  /** 错误码 (可选,小字展示) */
  code?: string;
  /** 是否显示"重试"按钮 */
  onRetry?: () => void;
  /** 自定义"主操作"按钮文字 + 行为 (覆盖 onRetry) */
  primaryAction?: { label: string; onClick: () => void };
  /** 是否显示"回到首页",默认 true */
  showHomeButton?: boolean;
  /** 渐变背景,默认 true; false 时给纯白背景 (例如详情页内联错误) */
  fullscreenGradient?: boolean;
}

export default function ErrorPage({
  icon,
  title,
  message,
  code,
  onRetry,
  primaryAction,
  showHomeButton = true,
  fullscreenGradient = true,
}: Props) {
  const navigate = useNavigate();

  const wrapClass = fullscreenGradient
    ? 'min-h-screen flex items-center justify-center bg-gradient-to-br from-gold-700 to-gold-500 p-4'
    : 'min-h-screen flex items-center justify-center p-4';

  return (
    <div className={wrapClass}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-4 text-gold-400">
          {icon || <AlertTriangle className="w-12 h-12" strokeWidth={1.5} />}
        </div>
        <h2 className="text-xl font-bold mb-2">{title}</h2>
        {message && (
          <p className="text-gray-600 mb-2 whitespace-pre-line">{message}</p>
        )}
        {code && <p className="text-xs text-gray-400 mb-6">错误码:{code}</p>}
        {!code && <div className="mb-6" />}

        <div className="flex gap-3 justify-center flex-wrap">
          {primaryAction && (
            <button
              className="px-5 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700"
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </button>
          )}
          {!primaryAction && onRetry && (
            <button
              className="px-5 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700"
              onClick={onRetry}
            >
              重试
            </button>
          )}
          {showHomeButton && (
            <button
              className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              onClick={() => navigate('/')}
            >
              回到首页
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
